import type { ElementType } from 'react';
import type { FunctionalityPermission } from '@/stores/authorization';

export interface PayloadMenuItem {
  key: string;
  name: string;
  path?: string;
  icon?: ElementType;
  children?: PayloadMenuItem[];
}

// /scale-management is an Outlet, not a standalone screen. A records-only
// profile must not get a blank destination after records move to their group.
const LAYOUT_ONLY_KEYS = new Set(['est_scale_management']);

export function buildPayloadMenuTree(
  functionalities: FunctionalityPermission[],
  normalizeRoute: (path: string) => string,
  icons: Record<string, ElementType> = {},
): PayloadMenuItem[] {
  const visible = functionalities.filter((f) => f.show_in_menu);
  const byKey = new Map<string, PayloadMenuItem>();
  const order = new Map(visible.map((f) => [f.key, f.menu_order]));

  for (const f of visible) {
    byKey.set(f.key, {
      key: f.key,
      name: f.name,
      path: f.route ? normalizeRoute(f.route) || undefined : undefined,
      icon: icons[f.key],
      children: [],
    });
  }

  const roots: PayloadMenuItem[] = [];
  for (const f of visible) {
    const item = byKey.get(f.key)!;
    const parent = f.parent_key ? byKey.get(f.parent_key) : undefined;
    if (parent) {
      parent.children!.push(item);
    } else {
      // A missing grouping permission must never hide an authorized screen.
      roots.push(item);
    }
  }

  const finalize = (items: PayloadMenuItem[]): PayloadMenuItem[] =>
    items
      .sort((a, b) => (order.get(a.key) ?? 0) - (order.get(b.key) ?? 0))
      .flatMap((item) => {
        const children = finalize(item.children ?? []);
        if (children.length) return [{ ...item, children }];
        // Menu-only groups cannot become links with an undefined destination
        // when a profile has no visible children (for example, after revocation).
        return item.path && !LAYOUT_ONLY_KEYS.has(item.key)
          ? [{ ...item, children: undefined }]
          : [];
      });

  return finalize(roots);
}
